import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { getEnvConfig } from '@/config/env.config';
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Elements, EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { api } from "@/services/auth-service";
import { loadStripe } from '@stripe/stripe-js';
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from 'react';

const config = getEnvConfig();

const StripeCheckoutSheet = ({ isOpen, onClose, priceId, isTrial }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [stripePromise] = useState(() => loadStripe(config.STRIPE_API_KEY));
  const { toast } = useToast();

  useEffect(() => {
    const createCheckoutSession = async () => {
      if (!isOpen) return;

      try {
        setIsLoading(true);
        const response = await api.post('/payments/create-checkout-session/', { priceId, trial: isTrial })
        setClientSecret(response.data.clientSecret);
      } catch (error) {
        console.error('Error:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Unable to initialize checkout. Please try again later.",
        });
        onClose();
      } finally {
        setIsLoading(false);
      }
    };

    createCheckoutSession();
  }, [isOpen, priceId, isTrial]);

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-full p-0 sm:max-w-none"
      >
        <div className="flex flex-col h-full">
          <div className="p-4 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50 border-b">
          <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="absolute top-0 left-0 border-none rounded-none h-14 w-14 bg-slate-200 dark:bg-slate-800 hover:bg-background/80"
            >
              <ArrowLeft className="h-7 w-7" />
            </Button>
            <h2 className="font-semibold text-center">
              {isTrial ? "Start Your Free Trial" : "Complete Your Purchase"}
            </h2>
          </div>

          <div className={cn(
            "flex-1 overflow-y-auto",
            "bg-gradient-to-b from-background to-muted/30"
          )}>
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-8 h-8 border-4 rounded-full animate-spin border-primary border-t-transparent"></div>
              </div>
            ) : clientSecret ? (
              <Elements stripe={stripePromise}>
                <EmbeddedCheckoutProvider
                  stripe={stripePromise}
                  options={{ clientSecret }}
                >
                  <div className="h-full">
                    <EmbeddedCheckout className="h-full" />
                  </div>
                </EmbeddedCheckoutProvider>
              </Elements>
            ) : null}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default StripeCheckoutSheet;
